import {
  findCatalogPage,
  type AgentCatalogPage,
} from '@/components/nami/agentPageCatalog';
import { useMemo } from 'react';
import { useRouter } from '@tanstack/react-router';
import { useGT } from 'gt-react';
import { z } from 'zod';
import { Card } from '@/components/ui/card';
import { NavButtonToolResult } from './NavButtonToolResult';

// Mirrors the backend render_section_card inputSchema. Local (not shared via
// @erp/rpc) because it's tiny and this is the only consumer.
const sectionCardArgsSchema = z.object({
  title: z.string(),
  description: z.string().optional(),
  items: z
    .array(z.object({ label: z.string(), value: z.string() }))
    .max(8)
    .optional(),
  navPath: z.string().optional(),
  navLabel: z.string().optional(),
});

interface SectionCardToolResultProps {
  /** The `render_section_card` tool-call args. */
  args: Record<string, unknown>;
  /** Navigable pages catalog; nav button only renders for a listed path. */
  pages: AgentCatalogPage[];
}

/**
 * Inline generative-UI card: renders the AI's `render_section_card` spec as a
 * titled summary with optional key/value facts and an optional button that
 * deep-links into an app section (shadcn Card + Tailwind only).
 */
export function SectionCardToolResult({
  args,
  pages,
}: SectionCardToolResultProps) {
  const gt = useGT();
  const router = useRouter();
  const parsed = useMemo(() => sectionCardArgsSchema.safeParse(args), [args]);

  // Only offer navigation to a path that's actually in the user's catalog —
  // drop hallucinated/inaccessible paths rather than render a broken button.
  const navMatch = useMemo(() => {
    if (!parsed.success || !parsed.data.navPath) return undefined;
    return findCatalogPage(router, pages, parsed.data.navPath);
  }, [router, parsed, pages]);

  if (!parsed.success) {
    return (
      <Card>
        <p className="text-muted-foreground text-sm">
          {gt('Unable to render summary.', {
            context: 'shown when an AI-generated section card spec is invalid',
          })}
        </p>
      </Card>
    );
  }

  const { title, description, items, navLabel } = parsed.data;

  return (
    <Card>
      <div className="text-card-foreground text-sm font-semibold">{title}</div>

      {description && (
        <p className="text-muted-foreground text-sm">{description}</p>
      )}

      {items && items.length > 0 && (
        <dl className="flex flex-col gap-1.5">
          {items.map((item, i) => (
            <div
              key={`${item.label}-${i}`}
              className="flex items-baseline justify-between gap-3"
            >
              <dt
                className="text-muted-foreground truncate text-xs"
                title={item.label}
              >
                {item.label}
              </dt>
              <dd className="text-card-foreground text-sm tabular-nums">
                {item.value}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {navMatch && (
        <div>
          <NavButtonToolResult
            // The concrete path: a record page's catalog entry is a pattern.
            to={parsed.data.navPath ?? navMatch.path}
            label={navLabel || navMatch.label}
          />
        </div>
      )}
    </Card>
  );
}
